import React from "react";
import NotFoundDatas from "components/RFQ/NotFoundDatas/NotFoundDatas";
import RfqCard from "components/RFQ/RFQCard/RFQCard";
import RfqList from "pages/SupplierFlow/RfqOverviews/RfqList";
import { RfxListPage } from "../RfxListPage";
import {
	getBidderRfxListSurface,
	type BidderRfxListItem,
	type BidderRfxTab,
} from "../surfaces/bidder-rfx-list-surface";

interface BidderTendersQuotesTabProps {
	companyId?: string;
	tab?: string;
}

/**
 * Bidder Tenders & Quotes list.
 *
 * This was a parallel implementation of the procurer list — its own tab
 * control, its own manual fetch/unwrap, its own infinite scroll, and no sort or
 * pagination. It now renders the SHARED shell (`RfxListPage`) via a surface
 * descriptor, so both sides get the same chrome, search, sort and paging from
 * one place. What stays bidder-specific is the two things that genuinely are:
 * the tab keys (submission statuses, see `bidder-rfx-list-surface.ts`) and the
 * row rendering, which is handed to the shell's `renderList` slot below and
 * still uses the existing `RfqCard`/`RfqList` components unchanged.
 *
 * The infinite scroll is deliberately NOT reproduced. It was the reason this
 * view accumulated rows across pages and could not offer a page control at all;
 * the shell's `Pagination` replaces it.
 */
const surface = getBidderRfxListSurface();

export const BidderTendersQuotesTab = ({
	companyId,
	tab,
}: BidderTendersQuotesTabProps) => (
	<RfxListPage<BidderRfxListItem, BidderRfxTab>
		companyId={companyId}
		surface={surface}
		tab={tab}
		renderEmptyState={() => (
			<div className="flex items-center justify-center">
				<NotFoundDatas text="No RFxs found" noDesc />
			</div>
		)}
		renderList={(rows, meta, view) =>
			view === "list" ? (
				<RfqList data={rows} isLoading={false} isLoadingPagination={false} />
			) : (
				<div
					className="grid w-full grid-cols-1 gap-6 lg:grid-cols-2 xlg:grid-cols-3 3xl:grid-cols-4 4xl:grid-cols-6"
					data-testid="bidder-tenders-quotes-grid"
				>
					{rows.map((row, index) => (
						<RfqCard key={row.rfqId ?? index} data={row} />
					))}
				</div>
			)
		}
	/>
);
